import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Images,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Copy,
  Send,
  Calendar,
  Users,
  Mail,
  Key,
  Upload,
  Zap,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleriesPage = () => {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [optimizing, setOptimizing] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, gallery: null });
  const [editDialog, setEditDialog] = useState({ open: false, gallery: null });
  const [editForm, setEditForm] = useState({ name: '', client_name: '', client_email: '', event_type: 'sports', event_date: '', expires_at: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditDialog = (gallery) => {
    setEditForm({
      name: gallery.name || '',
      client_name: gallery.client_name || '',
      client_email: gallery.client_email || '',
      event_type: gallery.event_type || 'sports',
      event_date: gallery.event_date ? gallery.event_date.split('T')[0] : '',
      expires_at: gallery.expires_at ? gallery.expires_at.split('T')[0] : ''
    });
    setEditDialog({ open: true, gallery });
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) {
      toast.error('Gallery name is required');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/galleries/${editDialog.gallery.id}`, {
        name: editForm.name,
        event_type: editForm.event_type,
        client_name: editForm.client_name || null,
        client_email: editForm.client_email || null,
        event_date: editForm.event_date || null,
        expires_at: editForm.expires_at || null
      });
      toast.success('Gallery updated');
      setEditDialog({ open: false, gallery: null });
      fetchGalleries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update gallery');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, [statusFilter, eventTypeFilter]);

  const fetchGalleries = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (eventTypeFilter !== 'all') params.append('event_type', eventTypeFilter);
      
      const response = await axios.get(`${API}/galleries?${params}`);
      setGalleries(response.data);
    } catch (error) {
      toast.error('Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (galleryId, newStatus) => {
    try {
      await axios.put(`${API}/galleries/${galleryId}/status?status=${newStatus}`);
      toast.success(`Gallery ${newStatus === 'published' ? 'published' : 'updated'}`);
      fetchGalleries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.gallery) return;
    try {
      await axios.delete(`${API}/galleries/${deleteDialog.gallery.id}`);
      toast.success('Gallery deleted');
      setDeleteDialog({ open: false, gallery: null });
      fetchGalleries();
    } catch (error) {
      toast.error('Failed to delete gallery');
    }
  };

  const copyAccessLink = (accessCode) => {
    const link = `${window.location.origin}/gallery/${accessCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Gallery link copied to clipboard');
  };

  const resendPasswordLink = async (clientEmail) => {
    if (!clientEmail) {
      toast.error('No client email associated with this gallery');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/resend-client-password-link?email=${encodeURIComponent(clientEmail)}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send password link');
    }
  };

  const filteredGalleries = galleries.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    g.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    g.client_email?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'sports': return 'bg-blue-100 text-blue-700';
      case 'school': return 'bg-purple-100 text-purple-700';
      case 'portrait': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Galleries</h1>
          <p className="text-slate-600 mt-1">Manage your client photo galleries</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setOptimizing(true);
              try {
                const res = await axios.post(`${API}/admin/regenerate-thumbnails`, {}, { timeout: 300000 });
                const d = res.data;
                if (d.photos_missing_thumbnails === 0) {
                  toast.success('All photos already have fast-loading thumbnails');
                } else {
                  toast.success(`Generated ${d.fixed} missing thumbnail(s)${d.failed ? `, ${d.failed} failed` : ''}`, { duration: 8000 });
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to optimize photos');
              }
              setOptimizing(false);
            }}
            disabled={optimizing}
            data-testid="optimize-photos-btn"
          >
            {optimizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            {optimizing ? 'Optimizing...' : 'Optimize Photos'}
          </Button>
          <Link to="/admin/galleries/bulk">
            <Button variant="outline" data-testid="bulk-import-btn">
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>
          </Link>
          <Link to="/admin/galleries/new">
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-gallery-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Gallery
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search galleries, clients..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-galleries-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="event-type-filter">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="sports">Sports</SelectItem>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="portrait">Portrait</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Gallery List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredGalleries.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGalleries.map((gallery) => (
            <Card key={gallery.id} className="border-slate-200 card-hover overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="font-heading text-lg truncate">{gallery.name}</CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge className={getStatusColor(gallery.status)}>
                        {gallery.status}
                      </Badge>
                      <Badge className={getEventTypeColor(gallery.event_type)}>
                        {gallery.event_type}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`gallery-menu-${gallery.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/galleries/${gallery.id}`)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(gallery)} data-testid={`edit-gallery-${gallery.id}`}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyAccessLink(gallery.access_code)}>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Link
                      </DropdownMenuItem>
                      {gallery.client_email && (
                        <DropdownMenuItem onClick={() => resendPasswordLink(gallery.client_email)}>
                          <Key className="mr-2 h-4 w-4" />
                          Resend Password Link
                        </DropdownMenuItem>
                      )}
                      {gallery.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleStatusChange(gallery.id, 'published')}>
                          <Send className="mr-2 h-4 w-4" />
                          Publish
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => setDeleteDialog({ open: true, gallery })}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {gallery.client_name && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Users className="h-4 w-4" />
                      <span className="truncate">{gallery.client_name}</span>
                    </div>
                  )}
                  {gallery.event_date && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(gallery.event_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Images className="h-4 w-4" />
                    <span>{gallery.photo_count} photos</span>
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                        {gallery.access_code}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyAccessLink(gallery.access_code)}
                        data-testid={`copy-link-${gallery.id}`}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Images className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No galleries found</h3>
            <p className="text-slate-500 mb-6">
              {search || statusFilter !== 'all' || eventTypeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first gallery to get started'}
            </p>
            <Link to="/admin/galleries/new">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Create Gallery
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, gallery: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gallery</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.gallery?.name}"? This will also delete all photos in this gallery. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, gallery: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Gallery Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => { if (!open) setEditDialog({ open: false, gallery: null }); }}>
        <DialogContent data-testid="edit-gallery-dialog">
          <DialogHeader>
            <DialogTitle>Edit Gallery</DialogTitle>
            <DialogDescription>Update the gallery details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Gallery Name</Label>
              <Input id="edit-name" value={editForm.name} data-testid="edit-gallery-name-input"
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-client-name">Client Name</Label>
              <Input id="edit-client-name" value={editForm.client_name} data-testid="edit-gallery-client-name-input"
                onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-client-email">Client Email</Label>
              <Input id="edit-client-email" type="email" value={editForm.client_email} data-testid="edit-gallery-client-email-input"
                onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Type</Label>
                <Select value={editForm.event_type} onValueChange={(v) => setEditForm({ ...editForm, event_type: v })}>
                  <SelectTrigger data-testid="edit-gallery-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sports">Sports</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-event-date">Event Date</Label>
                <Input id="edit-event-date" type="date" value={editForm.event_date}
                  onChange={(e) => setEditForm({ ...editForm, event_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expires-at">Gallery Expiry Date</Label>
              <Input id="edit-expires-at" type="date" value={editForm.expires_at} data-testid="edit-gallery-expires-input"
                onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })} />
              <p className="text-xs text-slate-500">Leave empty for no expiry. Clients can't access the gallery after this date.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, gallery: null })}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={savingEdit} data-testid="save-gallery-edit-btn">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleriesPage;
